import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { submitContactRequest } from '../services/contactService';
import { setPageSeo } from '../utils/seo';
import './Home.css';

// Landing page for restaurants (the platform's own home page — guests never land
// here, they book through the widget or the experience pages). Layout follows the
// approved designs: desktop at >= 1024px, phone at <= 640px, a stacked middle.

const PAGE_TITLE = 'ez restaurants | Online reservations, deposits & guest lists for restaurants in Egypt';
const PAGE_DESCRIPTION = 'ez restaurants by ez11 — online reservations, deposits, waitlists, guest lists and experiences for restaurants in Egypt. A booking widget for your website, a dashboard for your team and a portal for your guests.';
const CONTACT_EMAIL = 'hello@ez11.com';
const ADMIN_URL = (process.env.REACT_APP_ADMIN_URL || '').trim();
const PHONE_QUERY = '(max-width: 640px)';

const SECTION_LINKS = [
  { id: 'features', label: 'Features' },
  { id: 'guests', label: 'For guests' },
  { id: 'how', label: 'How it works' },
  { id: 'contact', label: 'Contact' },
];

// Each card carries the desktop copy (`text`) and the shorter phone copy (`short`)
// exactly as the two designs have them.
const FEATURES = [
  {
    title: 'Online reservations',
    text: 'Shifts, time slots and capacity limits per slot. Guests book in seconds from the widget on your site.',
    short: 'Shifts, time slots and capacity limits. Guests book in seconds from your site.',
    icon: (
      <>
        <rect x="3" y="4" width="18" height="17" rx="3" />
        <path d="M8 2v4M16 2v4M3 10h18" />
      </>
    ),
  },
  {
    title: 'Waitlist and approvals',
    text: 'Busy slots go to a waitlist; large parties or special slots wait for your approval before they are confirmed.',
    short: 'Busy slots go to a waitlist; special slots wait for your approval.',
    icon: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
  },
  {
    title: 'Deposits and payments',
    text: 'Per-guest or per-booking deposits through Paymob or Bank Misr, payment links, refunds and promo codes.',
    short: 'Paymob or Bank Misr deposits, payment links, refunds and promo codes.',
    icon: (
      <>
        <rect x="2" y="6" width="20" height="13" rx="3" />
        <path d="M2 10h20M6 15h4" />
      </>
    ),
  },
  {
    title: 'Experiences',
    text: 'Run special nights and events with booking types, seating templates, invitations and QR check-in.',
    short: 'Special nights and events with booking types, seating templates, invitations and QR check-in.',
    icon: (
      <>
        <path d="M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v1a2 2 0 0 0 0 4v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1a2 2 0 0 0 0-4Z" />
        <path d="M13 7v10" />
      </>
    ),
  },
  {
    title: 'Floor plan',
    text: 'Draw your zones and tables, assign reservations to them, and see who is seated where on any day.',
    short: 'Draw zones and tables and assign reservations to them.',
    icon: (
      <>
        <rect x="3" y="3" width="8" height="8" rx="2" />
        <rect x="13" y="3" width="8" height="8" rx="2" />
        <rect x="3" y="13" width="8" height="8" rx="2" />
        <rect x="13" y="13" width="8" height="8" rx="2" />
      </>
    ),
  },
  {
    title: 'Guest profiles and ratings',
    text: "Every guest's history, tags and notes in one place, with ratings collected automatically after each visit.",
    short: "Every guest's history, tags and ratings in one place.",
    icon: (
      <>
        <path d="M4 21v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </>
    ),
  },
  {
    title: 'Emails and SMS, your way',
    text: 'Confirmations, reminders and payment links go out automatically, from templates you edit with a live preview.',
    short: 'Automatic confirmations and reminders from templates you edit.',
    icon: (
      <>
        <path d="M4 4h16v12H7l-3 3z" />
        <path d="M8 9h8M8 12h5" />
      </>
    ),
  },
  {
    title: 'Memberships',
    text: 'Sell membership plans and let members pay and renew online, with their benefits recognised at booking time.',
    short: 'Sell plans online and recognise members when they book.',
    icon: <path d="M12 2l2.4 5 5.6.8-4 3.9.9 5.6-4.9-2.6L7.1 17.3 8 11.7 4 7.8 9.6 7z" />,
  },
  {
    title: 'Reports and transactions',
    text: 'Daily capacity, payment reports, refunds and transaction exports — with organisations and team roles for restaurant groups.',
    short: 'Capacity, payments and exports, with team roles for groups.',
    icon: <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />,
  },
];

const GUEST_BENEFITS = [
  {
    title: 'Book in seconds',
    text: "Date, time, guests, done — on any phone, in your restaurant's colours.",
    short: 'Date, time, guests — on any phone.',
    icon: <path d="M13 2 3 14h8l-1 8 10-12h-8z" />,
  },
  {
    title: 'Pay a deposit safely',
    text: 'Secure card payment with a clear receipt and refund rules.',
    short: 'Secure card payment, clear refund rules.',
    icon: <path d="M12 22s8-4 8-10V6l-8-3-8 3v6c0 6 8 10 8 10z" />,
  },
  {
    title: 'Confirmations and reminders',
    text: 'By email and SMS, with the house rules and directions included.',
    short: 'By email and SMS, with house rules.',
    icon: <path d="M4 4h16v12H7l-3 3z" />,
  },
  {
    title: 'A guest portal',
    text: 'Change the date or guests, cancel, or ask the venue a question — no phone call.',
    short: 'Change, cancel or ask — no phone call.',
    icon: (
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21a8 8 0 0 1 16 0" />
      </>
    ),
  },
];

const STEPS = [
  {
    title: 'We set up your restaurant',
    text: 'Shifts, capacity, zones, deposits and your email templates — configured with you in a short onboarding call.',
    short: 'Shifts, capacity, zones, deposits and templates, configured with you.',
  },
  {
    title: 'You add one line to your site',
    text: 'Add the booking widget to your website, or share your booking page on Instagram.',
    short: 'Add the booking widget to your site, or share your booking page on Instagram.',
  },
  {
    title: 'Bookings arrive in your dashboard',
    text: 'Your team sees every reservation, seats guests on the floor plan and gets paid — from any device.',
    short: 'Your team seats guests and gets paid, from any device.',
  },
];

const MOCK_DATES = [
  { day: 'Wed', date: 'Sep 30' },
  { day: 'Thu', date: 'Oct 1', state: 'on' },
  { day: 'Fri', date: 'Oct 2' },
  { day: 'Sat', date: 'Oct 3', state: 'full' },
  { day: 'Sun', date: 'Oct 4' },
  { day: 'Mon', date: 'Oct 5', state: 'exp' },
];
const MOCK_TIMES = ['7:00 pm', '7:30 pm', '8:00 pm', '8:30 pm'];
const MOCK_GUESTS = [1, 2, 3, 4, 5, 6];

const EMPTY_FORM = { name: '', restaurant: '', phone: '', message: '', website: '' };

const Icon = ({ children }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    {children}
  </svg>
);

const CheckIcon = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const getIsPhone = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(PHONE_QUERY).matches;

// The phone design uses shorter copy than the desktop one. Layout is all CSS; this
// only picks which text to render, on the same breakpoint as the phone layout.
const useIsPhone = () => {
  const [isPhone, setIsPhone] = useState(getIsPhone);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(PHONE_QUERY);
    const update = () => setIsPhone(mq.matches);
    update();
    if (mq.addEventListener) {
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    }
    mq.addListener(update);
    return () => mq.removeListener(update);
  }, []);

  return isPhone;
};

// Static picture of the booking widget, as drawn in the design. Decorative only.
const WidgetMock = () => (
  <div className="lp-mock" aria-hidden="true">
    <div className="lp-mock-glow" />
    <div className="lp-mock-card">
      <div className="lp-mock-cover">
        <div className="lp-mock-venue">
          <div className="lp-mock-name">Your Restaurant</div>
          <div className="lp-mock-pills">
            <span className="lp-mock-pill">Zamalek, Cairo</span>
            <span className="lp-mock-pill lp-no-phone">+20 100 234 5678</span>
          </div>
        </div>
      </div>
      <div className="lp-mock-body">
        <div className="lp-mock-head">
          <div className="lp-mock-title">Reserve at Your Restaurant</div>
          <div className="lp-mock-steps lp-no-phone">
            <span className="lp-mock-step lp-mock-step-on">1</span>
            <span className="lp-mock-step-line" />
            <span className="lp-mock-step">2</span>
          </div>
        </div>
        <div className="lp-mock-group">
          <div className="lp-mock-label">Select a date:</div>
          <div className="lp-mock-tiles">
            {MOCK_DATES.map((d) => (
              <div key={d.date} className={`lp-tile${d.state ? ` lp-tile-${d.state}` : ''}`}>
                {d.state === 'exp' && <span className="lp-tile-badge lp-no-phone">EXPERIENCE</span>}
                <small>{d.day}</small>
                <b>{d.date}</b>
              </div>
            ))}
          </div>
        </div>
        <div className="lp-mock-group">
          <div className="lp-mock-label">Select a time:</div>
          <div className="lp-mock-chips">
            {MOCK_TIMES.map((t) => (
              <span key={t} className={`lp-chip${t === '8:00 pm' ? ' lp-chip-on' : ''}`}>
                {t}
              </span>
            ))}
            <span className="lp-chip lp-chip-wait lp-no-phone">9:30 pm · waitlist</span>
          </div>
        </div>
        <div className="lp-mock-group lp-no-phone">
          <div className="lp-mock-label">Table for</div>
          <div className="lp-mock-chips">
            {MOCK_GUESTS.map((n) => (
              <span key={n} className={`lp-chip lp-chip-round${n === 4 ? ' lp-chip-on' : ''}`}>
                {n}
              </span>
            ))}
          </div>
        </div>
        <div className="lp-mock-deposit lp-no-phone">
          A deposit of (4 × EGP 500.00) is required to confirm your reservation, total is <b>EGP 2000.00</b>.
        </div>
        <div className="lp-mock-next">Next</div>
      </div>
    </div>
    <div className="lp-mock-toast">
      <span className="lp-mock-toast-icon">
        <CheckIcon size={16} />
      </span>
      <div>
        <b>New reservation</b>
        <div className="lp-mock-toast-meta">Mariam A. · Thu 8:00 pm · 4 guests</div>
      </div>
    </div>
  </div>
);

const Home = () => {
  const isPhone = useIsPhone();
  const [menuOpen, setMenuOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [formError, setFormError] = useState('');

  useEffect(() => {
    setPageSeo({ title: PAGE_TITLE, description: PAGE_DESCRIPTION, canonicalPath: '/' });
  }, []);

  const closeMenu = () => setMenuOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;

    const data = {
      name: form.name.trim(),
      restaurant: form.restaurant.trim(),
      phone: form.phone.trim(),
      message: form.message.trim(),
      // Honeypot: hidden from people, so it stays '' unless a bot fills it in.
      website: form.website,
    };
    if (!data.name || !data.phone || !data.message) {
      setFormError('Please fill in your name, phone and message.');
      setStatus('idle');
      return;
    }

    setFormError('');
    setStatus('sending');
    try {
      await submitContactRequest(data);
      setStatus('success');
    } catch (err) {
      setStatus('error');
    }
  };

  const errorText = formError || (status === 'error' ? `We couldn't send your message. Please email us at ${CONTACT_EMAIL}.` : '');

  return (
    <div className="lp">
      <header className="lp-nav">
        <Link to="/" className="lp-logo">
          ez11
        </Link>
        <nav className="lp-nav-links" aria-label="Main">
          {SECTION_LINKS.map((l) => (
            <a key={l.id} href={`#${l.id}`}>
              {l.label}
            </a>
          ))}
        </nav>
        <div className="lp-nav-actions">
          {ADMIN_URL && (
            <a href={ADMIN_URL} className="lp-btn lp-btn-ghost lp-btn-sm lp-signin">
              Sign in
            </a>
          )}
          <a href="#contact" className="lp-btn lp-btn-primary lp-btn-sm">
            Request a demo
          </a>
          <button
            type="button"
            className="lp-menu-btn"
            aria-label="Menu"
            aria-expanded={menuOpen}
            aria-controls="lp-menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
        </div>
        <nav id="lp-menu" className="lp-menu" aria-label="Menu" hidden={!menuOpen}>
          {SECTION_LINKS.map((l) => (
            <a key={l.id} href={`#${l.id}`} onClick={closeMenu}>
              {l.label}
            </a>
          ))}
        </nav>
      </header>

      <main>
        <section className="lp-hero" aria-labelledby="lp-hero-title">
          <div className="lp-hero-text">
            <p className="lp-kicker lp-kicker-gold">ez11 · Reservations platform for restaurants</p>
            <h1 id="lp-hero-title">Reservations, experiences and payments — on your own website.</h1>
            <p className="lp-hero-lead">
              {isPhone
                ? 'A booking widget for your site, a dashboard for your team and a portal for your guests. One line of code, and the bookings start coming in.'
                : 'ez11 gives your restaurant a booking widget, a dashboard for your team and a portal for your guests. One line of code on your site, and the bookings start coming in.'}
            </p>
            <div className="lp-hero-ctas">
              <a href="#contact" className="lp-btn lp-btn-gold">
                Request a demo
              </a>
              <a href="#how" className="lp-btn lp-btn-glass">
                See how it works
              </a>
            </div>
            <ul className="lp-hero-points">
              <li>Set up in days, not months</li>
              <li>Paymob and Bank Misr ready</li>
              <li>Support when you need it</li>
            </ul>
          </div>
          <WidgetMock />
        </section>

        <section id="features" className="lp-features" aria-labelledby="lp-features-title">
          <div className="lp-section-head">
            <p className="lp-kicker">What your restaurant gets</p>
            <h2 id="lp-features-title">Everything from the first click to the final bill</h2>
            <p className="lp-section-lead">
              {isPhone
                ? 'Take bookings, control your capacity, collect deposits and run experiences — from one dashboard your whole team can use.'
                : 'Take bookings, control your capacity, collect deposits and run experiences — all from one dashboard your whole team can use.'}
            </p>
          </div>
          <ul className="lp-feat-grid">
            {FEATURES.map((f) => (
              <li key={f.title} className="lp-card lp-feat">
                <div className="lp-icon">
                  <Icon>{f.icon}</Icon>
                </div>
                <div className="lp-feat-body">
                  <h3>{f.title}</h3>
                  <p>{isPhone ? f.short : f.text}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section id="guests" className="lp-guests" aria-labelledby="lp-guests-title">
          <div className="lp-guests-intro">
            <p className="lp-kicker">What your guests get</p>
            <h2 id="lp-guests-title">A booking they don't have to call about</h2>
            <p className="lp-guests-lead lp-no-phone">
              Guests book on your site, get a confirmation right away, and can manage the booking themselves — so your phone rings less.
            </p>
          </div>
          <ul className="lp-guest-grid">
            {GUEST_BENEFITS.map((g) => (
              <li key={g.title} className="lp-card lp-guest">
                <div className="lp-icon lp-icon-sm">
                  <Icon>{g.icon}</Icon>
                </div>
                <div className="lp-guest-body">
                  <h3>{g.title}</h3>
                  <p>{isPhone ? g.short : g.text}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section id="how" className="lp-how" aria-labelledby="lp-how-title">
          <div className="lp-section-head lp-how-head">
            <p className="lp-kicker">How it works</p>
            <h2 id="lp-how-title">Live on your website in three steps</h2>
          </div>
          <ol className="lp-steps">
            {STEPS.map((s, i) => (
              <li key={s.title} className="lp-card lp-step">
                <span className="lp-step-num" aria-hidden="true">
                  {i + 1}
                </span>
                <div className="lp-step-body">
                  <h3>{s.title}</h3>
                  <p>{isPhone ? s.short : s.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section id="contact" className="lp-contact" aria-labelledby="lp-contact-title">
          <div className="lp-contact-intro">
            <p className="lp-kicker lp-kicker-gold">Get started</p>
            <h2 id="lp-contact-title">Let's set up your restaurant</h2>
            {isPhone ? (
              <p className="lp-contact-lead">
                Tell us about your place and we'll reply within one working day. Or email us at{' '}
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
              </p>
            ) : (
              <>
                <p className="lp-contact-lead">
                  Tell us a little about your place and we'll get back to you within one working day with a demo and a plan.
                </p>
                <p className="lp-contact-email">
                  Or email us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                </p>
              </>
            )}
          </div>

          {status === 'success' ? (
            <div className="lp-form lp-form-done" role="status">
              <span className="lp-form-done-icon">
                <CheckIcon size={26} />
              </span>
              <p>Thanks! We got your message and will reply within one working day.</p>
            </div>
          ) : (
            <form className="lp-form" onSubmit={handleSubmit} noValidate>
              <div className="lp-form-row">
                <div>
                  <label className="lp-label" htmlFor="lp-name">
                    Your name
                  </label>
                  <input
                    id="lp-name"
                    className="lp-input"
                    type="text"
                    name="name"
                    placeholder="Mariam Adel"
                    autoComplete="name"
                    maxLength={120}
                    required
                    value={form.name}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="lp-label" htmlFor="lp-restaurant">
                    Restaurant
                  </label>
                  <input
                    id="lp-restaurant"
                    className="lp-input"
                    type="text"
                    name="restaurant"
                    placeholder="Your restaurant name"
                    autoComplete="organization"
                    maxLength={160}
                    value={form.restaurant}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div>
                <label className="lp-label" htmlFor="lp-phone">
                  Phone number
                </label>
                <input
                  id="lp-phone"
                  className="lp-input"
                  type="tel"
                  name="phone"
                  placeholder="+20 1xx xxx xxxx"
                  autoComplete="tel"
                  maxLength={40}
                  required
                  value={form.phone}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="lp-label" htmlFor="lp-message">
                  What would you like to do?
                </label>
                <textarea
                  id="lp-message"
                  className="lp-input"
                  name="message"
                  placeholder={isPhone ? 'Take reservations on our website…' : 'Take reservations on our website and bookings for our Friday nights…'}
                  maxLength={3000}
                  required
                  value={form.message}
                  onChange={handleChange}
                />
              </div>
              <input
                className="lp-hp"
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={form.website}
                onChange={handleChange}
              />
              <button type="submit" className="lp-btn lp-btn-primary lp-btn-block" disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending…' : 'Request a demo'}
              </button>
              {errorText && (
                <p className="lp-form-error" role="alert">
                  {errorText}
                </p>
              )}
              <p className="lp-form-note lp-no-phone">
                Your message goes straight to {CONTACT_EMAIL}. We reply within one working day.
              </p>
            </form>
          )}
        </section>
      </main>

      <footer className="lp-foot">
        <div className="lp-foot-top">
          <div className="lp-foot-brand">
            <p className="lp-foot-logo">ez11</p>
            <p className="lp-foot-desc">Reservations, experiences and payments for restaurants in Egypt.</p>
          </div>
          <nav className="lp-foot-cols" aria-label="Footer">
            <div className="lp-foot-col">
              <p className="lp-foot-title">Product</p>
              <a href="#features">Features</a>
              <a href="#guests">For guests</a>
              <a href="#how">How it works</a>
            </div>
            <div className="lp-foot-col">
              <p className="lp-foot-title">Company</p>
              <a href="https://ez11.com">About ez11</a>
              <a href="#contact">Contact</a>
            </div>
            <div className="lp-foot-col lp-foot-col-guests">
              <p className="lp-foot-title">Guests</p>
              <Link to="/portal">Find your booking</Link>
              <Link to="/portal">Guest portal</Link>
            </div>
          </nav>
        </div>
        <div className="lp-foot-bottom">
          <span>
            © {new Date().getFullYear()} ez11. All rights reserved. · An <a href="https://ez11.com">ez11</a> product
          </span>
          <span>
            Are you a guest looking for your reservation? <Link to="/portal">Go to the guest portal</Link>
          </span>
        </div>
      </footer>
    </div>
  );
};

export default Home;
